// src/views/BusinessSearchView.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  getDocs,
  where,
  doc,
  getDoc
} from 'firebase/firestore';
import { firestore } from '../views/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import MapComponent from './MapComponent';
import SettingsModal from './SettingsModal';
import CreateEventView from './CreateEventView';
import PostManagerView from './BusinessPostManagerView';
import BusinessEditor from '../components/BusinessEditor';
import useUserLocation from '../hooks/useUserLocation';
import LocationPrompt from '../components/LocationPrompt';
import AppToolbar from '../components/AppToolbar';
import BusinessOwnerSpeedDial from '../components/BusinessOwnerSpeedDial';
import FeedMapSwitch from '../components/FeedMapSwitch';
import mapLocationIcon from '../assets/android/ui/p4r_map_my_location.svg';
import tablesIcon from '../assets/android/ui/p4r_business_tables.svg';
import directionsIcon from '../assets/android/ui/p4r_business_directions.svg';
import callIcon from '../assets/android/ui/p4r_business_call.svg';
import emailIcon from '../assets/android/ui/p4r_business_email.svg';
import hoursIcon from '../assets/android/ui/p4r_business_hours.svg';
import offerAlcohol from '../assets/android/ui/p4r_business_offer_alcohol.svg';
import offerSmoke from '../assets/android/ui/p4r_business_offer_smoke.svg';
import offerFood from '../assets/android/ui/p4r_business_offer_food.svg';
import offer8 from '../assets/android/ui/p4r_business_offer_8ball.svg';
import offer9 from '../assets/android/ui/p4r_business_offer_9ball.svg';
import offerCarom from '../assets/android/ui/p4r_business_offer_carom.svg';
import offerRussian from '../assets/android/ui/p4r_business_offer_russian.svg';
import offerSnooker from '../assets/android/ui/p4r_business_offer_snooker.svg';
import MessagesModal from '../components/MessagesModal';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Accordion,
  Form,
  Card,
  Button,
  ListGroup,
  Modal,
  Badge,
  Alert
} from 'react-bootstrap';
import './BusinessSearchView.css';

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

const BUSINESS_OFFERINGS = [
  ['hasAlcohol','Alcohol',offerAlcohol],['hasSmokingLounge','Smoking lounge',offerSmoke],['hasFood','Food',offerFood],
  ['has8Ball','8 Ball',offer8],['has9Ball','9 Ball',offer9],['hasCarambola','Carom',offerCarom],['hasRussianPyramid','Russian Pyramid',offerRussian],['hasSnooker','Snooker',offerSnooker]
];


// ── Helpers for “open now” / hours display ─────────────────
function isOpenNow(hours = {}) {
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  return hours[today] && !hours[today].isClosed;
}

function renderHoursList(hours = {}) {
  const fmt = ts =>
    ts?.toDate
      ? ts.toDate().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      : '--:--';
  return Object.entries(hours).map(([day, info]) => (
    <ListGroup.Item
      key={day}
      className="bg-dark text-light border-secondary d-flex justify-content-between"
    >
      <strong>{day}</strong>
      {info.isClosed
        ? <span className="text-danger">Closed</span>
        : <span>{fmt(info.open)} – {fmt(info.close)}</span>}
    </ListGroup.Item>
  ));
}

export default function BusinessSearchView({
  isBusinessEditorOpen,
  setIsBusinessEditorOpen
}) {
  const { user, loading: authLoading } = useAuth();

  // ── Location logic from hook ───────────────────────────
  const {
    profileChecked,
    showLocationPrompt,
    userCenter,
    accuracy,
    permissionState,
    locationError,
    handleAllow,
    handleDeny,
    requestLocation,
    recenter
  } = useUserLocation();

  // ── Single activeModal string ──────────────────────────
  //    possible: null | 'settings' | 'createEvent' | 'postManager' | 'messages'
  const [activeModal, setActiveModal] = useState(null);

  // ── Business data & search state ───────────────────────
  const [businesses, setBusinesses]             = useState([]);
  const [searchOpen, setSearchOpen]             = useState(false);
  const [searchText, setSearchText]             = useState('');
  const [suggestions, setSuggestions]           = useState([]);
  const [predictions, setPredictions]           = useState([]);
  const [isSearching, setIsSearching]           = useState(false);
  const serviceRef                              = useRef(null);
  const inputRef                                = useRef(null);

  const [mapInstance, setMapInstance]           = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);

  const [hasBusinessProfile, setHasBusinessProfile] = useState(false);
  const [selectedBusiness, setSelectedBusiness]     = useState(null);
  const [loadingProfile, setLoadingProfile]         = useState(false);

  // ── Helper: geocode an address via google.maps.Geocoder ──
  const geocodeAddress = useCallback(address => {
    if (!window.google?.maps?.Geocoder) return Promise.resolve(null);
    return new Promise(resolve => {
      new window.google.maps.Geocoder().geocode(
        { address },
        (res, status) =>
          status === 'OK' && res[0]
            ? resolve({
                lat: res[0].geometry.location.lat(),
                lng: res[0].geometry.location.lng()
              })
            : resolve(null)
      );
    });
  }, []);

  // ── Check if current user has a business profile ─────────
  const checkBusinessProfile = useCallback(async () => {
    if (!user) return setHasBusinessProfile(false);
    const q = query(
      collection(firestore, 'businessprofiles'),
      where('businessId', '==', user.uid),
      where('businessStatus', '==', 'active')
    );
    const snap = await getDocs(q);
    setHasBusinessProfile(!snap.empty);
  }, [user]);

  // ── When user changes, re-check business profile ─────────
  useEffect(() => {
    if (!authLoading) checkBusinessProfile();
  }, [authLoading, checkBusinessProfile]);

  // ── Load & geocode all active businesses ─────────────────
  useEffect(() => {
    (async () => {
      const q = query(
        collection(firestore, 'businessprofiles'),
        where('businessStatus', '==', 'active')
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const withCoords = await Promise.all(
        docs.map(async b => {
          const point = b.location || b.coordinate || b.geoPoint;
          const directLat = Number(b.latitude ?? point?.latitude ?? point?.lat);
          const directLng = Number(b.longitude ?? point?.longitude ?? point?.lng);
          if (Number.isFinite(directLat) && Number.isFinite(directLng)) {
            return { ...b, latitude: directLat, longitude: directLng };
          }
          if (!b.address) return null;
          const loc = await geocodeAddress(b.address);
          return loc ? { ...b, latitude: loc.lat, longitude: loc.lng } : null;
        })
      );
      setBusinesses(withCoords.filter(Boolean));
    })();
  }, [geocodeAddress]);

  // ── Debounce local Firestore “suggestions” from `businesses` ──
  useEffect(() => {
    if (!searchText.trim()) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const id = setTimeout(() => {
      const lower = searchText.toLowerCase();
      setSuggestions(
        businesses.filter(b =>
          b.businessBrand.toLowerCase().includes(lower) ||
          b.address.toLowerCase().includes(lower)
        )
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchText, businesses]);

  // ── Pan-to-selected‐location when `selectedLocation` changes ──
  useEffect(() => {
    if (mapInstance && selectedLocation) {
      mapInstance.panTo(selectedLocation);
      mapInstance.setZoom(14);
      closeSearchPanel();
    }
  }, [mapInstance, selectedLocation]);

  // ── Init Google Places Autocomplete service once ──────────
  useEffect(() => {
    if (window.google?.maps?.places && !serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, []);

  // ── Fetch place predictions from Google when user types ────
  useEffect(() => {
    if (!searchOpen || !searchText.trim()) {
      setPredictions([]);
      return;
    }
    if (!window.google?.maps?.places) return;
    if (!serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
    }
    setIsSearching(true);

    const id = setTimeout(() => {
      serviceRef.current.getPlacePredictions(
        {
          input: searchText,
          types: ['establishment'],
          keyword: 'pool billiards'
        },
        (preds) => {
          setPredictions(preds || []);
          setIsSearching(false);
        }
      );
    }, 300);

    return () => clearTimeout(id);
  }, [searchText, searchOpen]);

  // ── Auto‐focus the search input when panel opens ─────────
  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  const handlePredictionClick = placeId => {
    if (!window.google?.maps?.Geocoder) return;
    new window.google.maps.Geocoder().geocode(
      { placeId },
      (results, status) => {
        if (status === 'OK' && results[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          setSelectedLocation({ lat: loc.lat(), lng: loc.lng() });
        }
      }
    );
    closeSearchPanel();
  };



  // ── “open” a business’s profile for detail ──────────────
  const handleBusinessSelect = async id => {
    setLoadingProfile(true);
    try {
      const snap = await getDoc(doc(firestore, 'businessprofiles', id));
      if (snap.exists()) {
        setSelectedBusiness({ id, ...snap.data() });
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const closeSearchPanel = () => {
    setSearchOpen(false);
    setSearchText('');
    setSuggestions([]);
    setPredictions([]);
    setIsSearching(false);
  };

  // ── Don’t render until auth + location are both ready ─────
  if (authLoading || !profileChecked) return null;

  return (
    <div className="business-search-view" style={{ position: 'relative' }}>
      {/* ── Search Panel ──────────────────────────────────── */}
      {searchOpen && (
        <div className="search-bar-floating">
          <Form.Control
            ref={inputRef}
            type="text"
            placeholder="Search bars, pool halls…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <Button variant="link" className="close-button" onClick={closeSearchPanel}>
            &times;
          </Button>
        </div>
      )}

      {/* ── Results Overlay ────────────────────────────────── */}
      {searchOpen && (
        <div className="results-overlay">
          {isSearching ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton-item">
                <div className="skeleton-avatar" />
                <div className="skeleton-text">
                  <div className="skeleton-line short" />
                  <div className="skeleton-line long" />
                </div>
              </div>
            ))
          ) : (suggestions.length > 0 || predictions.length > 0) ? (
            <>
              {suggestions.map(b => (
                <div
                  key={b.id}
                  className="result-item"
                  onClick={() => {
                    setHighlightedId(b.id);
                    setSelectedLocation({ lat: b.latitude, lng: b.longitude });
                    closeSearchPanel();
                  }}
                >
                  <img
                    src={b.profileImageUrl}
                    alt={b.businessBrand}
                    className="suggestion-img me-2"
                  />
                  <div>
                    <strong>{b.businessBrand}</strong>
                    <p className="m-0 small text-muted">{b.address}</p>
                  </div>
                </div>
              ))}

              {suggestions.length > 0 && predictions.length > 0 && (
                <hr className="divider" />
              )}

              {predictions.map(p => (
                <div
                  key={p.place_id}
                  className="result-item text-secondary"
                  onClick={() => handlePredictionClick(p.place_id)}
                >
                  <strong>{p.structured_formatting.main_text}</strong>
                  <small className="ms-2">{p.structured_formatting.secondary_text}</small>
                </div>
              ))}
            </>
          ) : (
            <Alert variant="dark" className="text-center m-4">
              No results yet...
            </Alert>
          )}
        </div>
      )}

      {/* ── Location Prompt ────────────────────────────────── */}
      <LocationPrompt
        show={showLocationPrompt}
        onAllow={handleAllow}
        onDeny={handleDeny}
        blocked={permissionState === 'denied'}
      />

      {/* ── Map ────────────────────────────────────────────── */}
      <MapComponent
        markers={businesses}
        center={selectedLocation || userCenter || DEFAULT_CENTER}
        userLocation={userCenter}
        accuracy={accuracy}
        highlightedId={highlightedId}
        onMapLoad={setMapInstance}
        onMapClick={() => setHighlightedId(null)}
        onMarkerClick={m => {
          setHighlightedId(m.id);
          setSelectedLocation({ lat: m.latitude, lng: m.longitude });
          handleBusinessSelect(m.id);
        }}
      />

      {permissionState !== 'granted' && (
        <div className={`p4r-location-banner ${permissionState === 'denied' ? 'blocked' : ''}`}>
          <div><strong>{permissionState === 'denied' ? 'Location is off' : 'Find what is near you'}</strong><span>{locationError || 'Enable location for nearby rooms, players, and tournaments.'}</span></div>
          <Button size="sm" onClick={requestLocation}>{permissionState === 'denied' ? 'Try again' : 'Enable'}</Button>
        </div>
      )}

      <FeedMapSwitch showingMap />

      {/* ── “My Location” Button ─────────────────────────── */}
      <Button
        className="recenter-btn"
        onClick={() => recenter(mapInstance)}
      >
        <img src={mapLocationIcon} alt="" /><span className="visually-hidden">My Location</span>
      </Button>

      {/* ── Business Profile Modal ────────────────────────── */}
      {selectedBusiness && (
        <Modal
          show
          onHide={() => setSelectedBusiness(null)}
          size="lg"
          centered
          contentClassName="bg-dark text-light"
        >
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>{selectedBusiness.businessBrand}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="p4r-business-detail-stat">
              <img src={tablesIcon} alt="" />
              <span>{selectedBusiness.poolTables || 0} Pool Table{(selectedBusiness.poolTables || 0) === 1 ? '' : 's'}</span>
            </div>

            <Card className="bg-secondary mb-3">
              <Card.Img
                variant="top"
                src={selectedBusiness.secondImageUrl || '/default-avatar.png'}
                style={{ borderRadius: '8px' }}
              />
            </Card>

            {selectedBusiness.businessBio && <p>{selectedBusiness.businessBio}</p>}

            <div className="d-flex justify-content-between mb-4">
              <Button
                variant="outline-light"
                className="flex-fill me-2"
                onClick={() =>
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      selectedBusiness.address
                    )}`,
                    '_blank'
                  )
                }
              >
                <img src={directionsIcon} alt="" /> <span>Directions</span>
              </Button>
              <Button
                variant="outline-light"
                className="flex-fill mx-2"
                onClick={() => (window.location = `tel:${selectedBusiness.phone}`)}
              >
                <img src={callIcon} alt="" /> <span>Call</span>
              </Button>
              <Button
                variant="outline-light"
                className="flex-fill ms-2"
                onClick={() => (window.location = `mailto:${selectedBusiness.businessEmail}`)}
              >
                <img src={emailIcon} alt="" /> <span>Email</span>
              </Button>
            </div>

            <div className="p4r-business-highlights">
              {BUSINESS_OFFERINGS.filter(([key]) => selectedBusiness.businessOfferingFields?.[key]).map(([key,label,icon]) => (
                <span key={key}><img src={icon} alt="" />{label}</span>
              ))}
            </div>

            <Accordion className="business-sign">
              <Accordion.Item eventKey="0">
                <Accordion.Header className="hours-header">
                  <span className="p4r-hours-heading"><img src={hoursIcon} alt="" />Business Hours</span>
                  <Badge
                    className={`hours-badge ms-auto ${
                      isOpenNow(selectedBusiness.businessHours) ? '' : 'closed'
                    }`}
                  >
                    {isOpenNow(selectedBusiness.businessHours) ? 'Open Now' : 'Closed'}
                  </Badge>
                </Accordion.Header>
                <Accordion.Body className="hours-body">
                  <ListGroup variant="flush">
                    {renderHoursList(selectedBusiness.businessHours)}
                  </ListGroup>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Modal.Body>
        </Modal>
      )}

      <BusinessOwnerSpeedDial visible={hasBusinessProfile} />

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <AppToolbar
        onNavigate={view => {
          setActiveModal(view);
        }}
        currentPage="business"
        onSearchToggle={setSearchOpen}
        searchOpen={searchOpen}
        onOpenMessages={() => setActiveModal('messages')}
      />

      {/* ── All route‐based modals (only one can open) ──────── */}
      {activeModal === 'settings'    && (
        <SettingsModal show onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'createEvent' && (
        <CreateEventView onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'postManager' && (
        <PostManagerView onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'messages'    && (
        <MessagesModal show onClose={() => setActiveModal(null)} />
      )}

      {/* ── Business Editor (controlled via prop) ──────────── */}
      {isBusinessEditorOpen && (
        <BusinessEditor
          show={true}
          onClose={() => setIsBusinessEditorOpen(false)}
        />
      )}
    </div>
  );
}
